import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useResponsiveImage } from '../hooks/useOptimizedImage';

interface PanPosition {
  x: number;
  y: number;
}

interface BackgroundEffectsProps {
  imageUrls: string[];
  active: boolean;
  postId: number;
}

const BgBlur = styled.div<{
  imageUrl?: string;
  opacity?: number;
  pan?: PanPosition;
  zIndex?: number;
}>`
  position: absolute;
  inset: 0;
  z-index: ${({ zIndex }) => zIndex ?? 0};
  background-image: ${({ imageUrl }) => imageUrl ? `url(${imageUrl})` : 'none'};
  background-size: cover;
  background-position: center;
  opacity: ${({ opacity }) => opacity ?? 1};
  ${({ pan }) => pan && `transform: scale(1.25) translate(${pan.x}%, ${pan.y}%);`}
  transition: opacity 3s, transform 7s linear;
  will-change: transform, opacity;
`;

// Optimized Background Image Component
const OptimizedBgBlur: React.FC<{
  imageUrl: string;
  opacity?: number;
  pan?: PanPosition;
  zIndex?: number;
  style?: React.CSSProperties;
}> = ({ imageUrl, opacity, pan, zIndex, style }) => {
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);
  
  // Detect mobile
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Use optimized image with large size for background
  const optimizedImage = useResponsiveImage(imageUrl, 'single-post-hero', isMobile);
  
  return (
    <BgBlur
      imageUrl={optimizedImage.src}
      opacity={opacity}
      pan={pan}
      zIndex={zIndex}
      style={style}
    />
  );
};

const BackgroundEffects: React.FC<BackgroundEffectsProps> = ({ imageUrls, active, postId }) => {
  // Pan: luôn chạy liên tục cho mọi background, không cooldown
  const [panMap, setPanMap] = useState<{ [idx: number]: PanPosition }>({});
  const panIntervalRef = useRef<number | null>(null);
  // Fade: cứ mỗi 7s đổi background (3s fade)
  const [bgIndex, setBgIndex] = useState(0);
  const [fadeState, setFadeState] = useState<'fade-in' | 'fade-out'>('fade-in');
  const fadeTimeoutRef = useRef<number | null>(null);

  // Tạo pan ngẫu nhiên
  const randomPan = (): PanPosition => {
    const x = Math.round((Math.random() - 0.5) * 16); // -8% đến 8%
    const y = Math.round((Math.random() - 0.5) * 16); // -8% đến 8%
    return { x, y };
  };

  // Main effect for pan and fade
  useEffect(() => {
    if (!active || imageUrls.length === 0) return;
    
    setBgIndex(0);
    setFadeState('fade-in');
    
    // Khởi tạo pan cho tất cả background
    const initialPan: { [idx: number]: PanPosition } = {};
    imageUrls.forEach((_, idx) => { initialPan[idx] = randomPan(); });
    setPanMap(initialPan);
    
    // Cleanup previous timers
    if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
    if (panIntervalRef.current) clearInterval(panIntervalRef.current);
    
    // Pan: luôn chạy mỗi 7s cho mọi background
    panIntervalRef.current = window.setInterval(() => {
      setPanMap(prev => {
        const next: { [idx: number]: PanPosition } = { ...prev };
        imageUrls.forEach((_, idx) => { next[idx] = randomPan(); });
        return next;
      });
    }, 7000);
    
    if (imageUrls.length === 1) {
      // Không fade, chỉ pan liên tục
      return () => { 
        if (panIntervalRef.current) clearInterval(panIntervalRef.current); 
      };
    } else {
      let running = true;
      let currentBgIdx = 0;
      
      const startFade = () => {
        if (!running) return;
        setFadeState('fade-out');
        window.setTimeout(() => {
          currentBgIdx = (currentBgIdx + 1) % imageUrls.length;
          setBgIndex(currentBgIdx);
          setFadeState('fade-in');
          if (running) fadeTimeoutRef.current = window.setTimeout(startFade, 7000); // 7s sau lại fade tiếp
        }, 3000); // 3s fade
      };
      
      fadeTimeoutRef.current = window.setTimeout(startFade, 7000); // 7s sau khi mount/đổi post
      
      return () => {
        running = false;
        if (panIntervalRef.current) clearInterval(panIntervalRef.current);
        if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
      };
    }
  }, [active, imageUrls, postId]);

  // Reset hiệu ứng khi đổi post - Đơn giản hóa logic
  useEffect(() => {
    setBgIndex(0);
    setFadeState('fade-in');
    // Khởi tạo pan cho tất cả background
    const initialPan: { [idx: number]: PanPosition } = {};
    imageUrls.forEach((_, idx) => { initialPan[idx] = randomPan(); });
    setPanMap(initialPan);
  }, [postId, imageUrls]);

  // Render background hiệu ứng - Tối ưu: chỉ render 2-3 layers cần thiết
  if (imageUrls.length === 0) return null;
  
  if (imageUrls.length === 1) {
    return (
      <OptimizedBgBlur
        key={imageUrls[0]}
        imageUrl={imageUrls[0]}
        opacity={1}
        pan={panMap[0] || { x: 0, y: 0 }}
        zIndex={0}
        style={{ transition: 'opacity 3s, transform 7s linear' }}
      />
    );
  }
  
  // Chỉ render 2 layers: current và previous, không render tất cả
  const currentIdx = bgIndex;
  const prevIdx = (bgIndex - 1 + imageUrls.length) % imageUrls.length;
  
  return (
    <>
      {/* Previous layer - fade out */}
      <OptimizedBgBlur
        key={`prev-${imageUrls[prevIdx]}`}
        imageUrl={imageUrls[prevIdx]}
        opacity={fadeState === 'fade-in' ? 0 : 1}
        pan={panMap[prevIdx] || { x: 0, y: 0 }}
        zIndex={0}
        style={{ transition: 'opacity 3s, transform 7s linear' }}
      />
      {/* Current layer - fade in */}
      <OptimizedBgBlur
        key={`curr-${imageUrls[currentIdx]}`}
        imageUrl={imageUrls[currentIdx]}
        opacity={fadeState === 'fade-in' ? 1 : 0}
        pan={panMap[currentIdx] || { x: 0, y: 0 }}
        zIndex={1}
        style={{ transition: 'opacity 3s, transform 7s linear' }}
      />
    </>
  );
};

export default BackgroundEffects; 